"use client";

import React, { useState, useEffect } from "react";
import { ComponentSchema } from "@/lib/ecoBuilder/schema/types";
import { cn } from "@/lib/utils";
import { RichTextRenderer } from '@/components/ecoBuilder/atoms/RichTextRenderer';
import { PaddingHandle } from "@/components/ecoBuilder/editor/PaddingHandle";
import { ChevronLeft, ChevronRight } from "lucide-react";

// ═══════════════════════════════════════════════════════════════
// Schema Definition
// ═══════════════════════════════════════════════════════════════
export const GallerySectionSchema: ComponentSchema = {
    // Content
    // Fallback Slots if image-list isn't fully supported in all inspectors
    image1: {
        type: "image",
        label: "صورة 1",
        description: "الصورة الأولى في المعرض"
    },
    image2: {
        type: "image",
        label: "صورة 2",
        description: "الصورة الثانية في المعرض"
    },
    image3: {
        type: "image",
        label: "صورة 3",
        description: "الصورة الثالثة في المعرض"
    },

    title: {
        type: "text",
        label: "العنوان الرئيسي",
        defaultValue: "معرض الصور",
    },
    subtitle: {
        type: "textarea",
        label: "العنوان الفرعي",
        defaultValue: "",
    },

    // Appearance
    height: {
        type: "select",
        label: "الارتفاع",
        defaultValue: "medium",
        options: [
            { value: "small", label: "صغير (300px)" },
            { value: "medium", label: "متوسط (500px)" },
            { value: "large", label: "كبير (700px)" },
        ],
    },
    autoPlay: {
        type: "boolean",
        label: "تشغيل تلقائي",
        defaultValue: true,
    },

    // Layout
    paddingTop: {
        type: "slider",
        label: "مسافة علوية",
        defaultValue: 60,
        sliderConfig: { min: 0, max: 200, step: 4, unit: "px" },
    },
    paddingBottom: {
        type: "slider",
        label: "مسافة سفلية",
        defaultValue: 60,
        sliderConfig: { min: 0, max: 200, step: 4, unit: "px" },
    },
};

export const GallerySectionDefaultProps = {
    title: "معرض الصور",
    subtitle: "تصفح مجموعتنا المميزة",
    height: "medium",
    autoPlay: true,
    paddingTop: 60,
    paddingBottom: 60,
};

// ═══════════════════════════════════════════════════════════════
// Component
// ═══════════════════════════════════════════════════════════════
export function GallerySection({
    image1, image2, image3, // Fallback inputs
    title,
    subtitle,
    height = "medium",
    autoPlay = true,
    paddingTop = 60,
    paddingBottom = 60,
    background_color, // Add this
    isSelected,
    onUpdateProps,
}: any) {

    // Combine array images with individual slots
    const validImages = [image1, image2, image3].filter(Boolean);
    const [currentIndex, setCurrentIndex] = useState(0);

    // Height Class
    const getHeightClass = () => {
        switch (height) {
            case "large": return "h-[700px]";
            case "medium": return "h-[500px]";
            case "small": return "h-[300px]";
            default: return "h-[500px]";
        }
    };

    // Auto Play
    useEffect(() => {
        if (!autoPlay || validImages.length <= 1 || isSelected) return; // Retrieve selection to pause?

        const timer = setInterval(() => {
            setCurrentIndex(prev => (prev + 1) % validImages.length);
        }, 5000);
        return () => clearInterval(timer);
    }, [autoPlay, validImages.length, isSelected]);

    const nextSlide = () => setCurrentIndex(prev => (prev + 1) % validImages.length);
    const prevSlide = () => setCurrentIndex(prev => (prev - 1 + validImages.length) % validImages.length);

    const handlePaddingChange = (side: 'top' | 'bottom') => (newVal: number) => {
        if (!onUpdateProps) return;
        if (side === 'top') onUpdateProps({ paddingTop: newVal });
        else onUpdateProps({ paddingBottom: newVal });
    };

    return (
        <section
            className={cn(
                "relative w-full overflow-hidden",
                isSelected ? "" : ""
            )}
            style={{
                paddingTop: `${paddingTop}px`,
                paddingBottom: `${paddingBottom}px`,
                backgroundColor: background_color || 'transparent',
            }}
        >
            {/* Header */}
            {(title || subtitle) && (
                <div className="container mx-auto px-4 mb-8 text-center relative z-10">
                    {title && <RichTextRenderer html={title} className="text-3xl font-bold mb-2" />}
                    {subtitle && <RichTextRenderer html={subtitle} className="text-slate-600 max-w-2xl mx-auto" />}
                </div>
            )}

            {/* Slider Container */}
            <div className={cn("relative group mx-auto w-[calc(100%-32px)] md:w-[calc(100%-64px)] md:max-w-7xl rounded-2xl shadow-sm overflow-hidden", getHeightClass())}>
                {validImages.length > 0 ? (
                    validImages.map((img: string, idx: number) => (
                        <div
                            key={idx}
                            className={cn(
                                "absolute inset-0 transition-opacity duration-700 ease-in-out bg-cover bg-center",
                                idx === currentIndex ? "opacity-100 z-10" : "opacity-0 z-0"
                            )}
                            style={{ backgroundImage: `url(${img})` }}
                        />
                    ))
                ) : (
                    // Empty State
                    <div className="absolute inset-0 flex items-center justify-center bg-slate-200 text-slate-400">
                        <div className="text-center">
                            <p className="text-xl font-medium">لا توجد صور</p>
                            <p className="text-sm">أضف صوراً 1، 2، 3 من لوحة الخصائص</p>
                        </div>
                    </div>
                )}

                {/* Navigation Arrows */}
                {validImages.length > 1 && (
                    <>
                        <button
                            onClick={prevSlide}
                            className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 bg-white/80 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-transform hover:scale-110"
                        >
                            <ChevronLeft className="w-6 h-6" />
                        </button>
                        <button
                            onClick={nextSlide}
                            className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 bg-white/80 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-transform hover:scale-110"
                        >
                            <ChevronRight className="w-6 h-6" />
                        </button>
                    </>
                )}

                {/* Dots */}
                {validImages.length > 1 && (
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 flex gap-2">
                        {validImages.map((_: any, idx: number) => (
                            <button
                                key={idx}
                                onClick={() => setCurrentIndex(idx)}
                                className={cn(
                                    "w-3 h-3 rounded-full transition-all",
                                    idx === currentIndex ? "bg-white w-8" : "bg-white/50 hover:bg-white/80"
                                )}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* Padding Handles */}
            {isSelected && onUpdateProps && (
                <>
                    <PaddingHandle
                        side="top"
                        currentPadding={paddingTop}
                        onPaddingChange={handlePaddingChange('top')}
                    />
                    <PaddingHandle
                        side="bottom"
                        currentPadding={paddingBottom}
                        onPaddingChange={handlePaddingChange('bottom')}
                    />
                </>
            )}
        </section>
    );
}
